import React from 'react';

export interface TabBarItem<T extends string = string> {
  id: T;
  label: string;
  disabled?: boolean;
}

export interface TabBarProps<T extends string = string> {
  items: TabBarItem<T>[];
  activeId: T;
  onChange: (id: T) => void;
  /** `primary` for top-of-canvas tabs, `sub` for nested sub-tabs inside a tab body. */
  variant?: 'primary' | 'sub';
  ariaLabel?: string;
}

/**
 * Canonical admin tab strip. Wraps the existing `.admin-tab-bar` chrome in a
 * declarative items[]/activeId/onChange API so call sites stop hand-rolling
 * the same `className === active ? ... : ...` ternary for every tab.
 */
export default function TabBar<T extends string = string>({
  items,
  activeId,
  onChange,
  variant = 'primary',
  ariaLabel,
}: TabBarProps<T>) {
  const className = [
    'admin-tab-bar',
    variant === 'sub' ? 'admin-tab-bar--sub' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className} role="tablist" aria-label={ariaLabel}>
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={item.disabled}
            className={
              isActive
                ? 'admin-tab-bar__tab admin-tab-bar__tab--active'
                : 'admin-tab-bar__tab'
            }
            onClick={() => onChange(item.id)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
